<template>
  <!-- Skip Navigation Link -->
  <a href="#main-content" class="skip-link" @focus="skipLinkFocused = true" @blur="skipLinkFocused = false">
    Skip to main content
  </a>

  <header role="banner" class="navbar">
    <div class="pallas-watermark">
      <div class="pallas-watermark-text"></div>
    </div>
    <div class="navbar-left">
      <!-- Mobile Menu Toggle -->
      <button
        class="mobile-menu-toggle"
        :class="{ 'active': mobileMenuOpen }"
        @click="toggleMobileMenu"
        :aria-expanded="mobileMenuOpen"
        aria-label="Toggle navigation menu"
        aria-controls="main-navigation"
      >
        <span class="hamburger-line"></span>
        <span class="hamburger-line"></span>
        <span class="hamburger-line"></span>
      </button>
      <router-link to="/HomePage" class="logo-link" aria-label="PALLAS - Navigate to homepage">
        <div class="logo-container">
          <div class="logo-gradient"></div>
          <img src="@/assets/logomark_white.svg" alt="PALLAS - Portal Linking Artists and Scientists" class="logo-img" />
        </div>
      </router-link>
      
      <nav class="nav-section" role="navigation" aria-label="Main navigation" id="main-navigation" :class="{ 'mobile-open': mobileMenuOpen }">
        <ul class="nav-links" role="menubar">
          <li role="none"><router-link to="/AboutUs" class="nav-link" role="menuitem" tabindex="0" @click="closeMobileMenu">About</router-link></li>
          <li role="none"><router-link to="/SearchArtists" class="nav-link" role="menuitem" tabindex="0" @click="closeMobileMenu">Gallery</router-link></li>
          <li role="none"><router-link to="/BlogPage" class="nav-link" role="menuitem" tabindex="0" @click="closeMobileMenu">Blog</router-link></li>
          <li role="none"><router-link to="/BestPractices" class="nav-link" role="menuitem" tabindex="0" @click="closeMobileMenu">FAQ</router-link></li>
        </ul>
      </nav>
    </div>
    <div class="navbar-right">
      <div v-if="!isLoggedIn" class="auth-section" role="group" aria-label="Authentication options">
        <button class="auth-btn login-btn" @click="openLoginModal" aria-label="Open login modal" tabindex="0">
          <span class="btn-text">Log In</span>
        </button>
        <button class="auth-btn signup-btn" @click="openSignUpModal" aria-label="Open sign up modal" tabindex="0">
          <span class="btn-text">Sign Up</span>
        </button>
      </div>
      <div v-else class="profile-section" role="group" aria-label="User profile options">
        <button
          class="auth-btn profile-btn"
          :class="{
            'scientist-profile': userInfo?.role === 'scientist',
            'artist-profile': userInfo?.role === 'artist',
            'both-profile': userInfo?.role === 'both' || !userInfo?.role || userInfo?.role === 'general'
          }"
          @click="goToProfile"
          :aria-label="`Go to profile dashboard - ${userInfo?.firstName || 'User'}`"
          tabindex="0"
        >
          <span class="btn-text">My Profile</span>
        </button>
        <button v-if="isAdmin" class="auth-btn admin-btn" @click="goToAdmin" aria-label="Go to admin dashboard" tabindex="0">
          <span class="btn-text">Admin</span>
        </button>
        <button class="auth-btn logout-btn" @click="handleLogout" aria-label="Logout from account" tabindex="0">
          <span class="btn-text">Logout</span>
        </button>
      </div>
    </div>
  </header>
</template>

<script>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useRouter } from 'vue-router';
import { auth } from '../firebase';
import { signOut, onAuthStateChanged } from 'firebase/auth';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import { openLoginModal, openSignUpModal } from '@/composables/useAuthModal';

export default {
  name: 'HeaderPage',
  setup() {
    const router = useRouter();
    const userInfo = ref(null);
    const isLoggedIn = ref(false);
    const db = getFirestore();
    let unsubscribe = null;

    // Mobile menu and accessibility state
    const mobileMenuOpen = ref(false);
    const skipLinkFocused = ref(false);

    const isAdmin = computed(() => userInfo.value?.adminRole === 'admin' || userInfo.value?.role === 'admin');
    const isVerified = computed(() => {
      if (!userInfo.value) return false;
      return String(userInfo.value.verified).toLowerCase() === 'true';
    });

    const fetchUserData = async (uid) => {
      const userDoc = doc(db, 'users', uid);
      const snapshot = await getDoc(userDoc);
      if (snapshot.exists()) {
        return snapshot.data();
      } else {
        throw new Error('User not found');
      }
    };


    onUnmounted(() => {
      if (unsubscribe) unsubscribe();
    });

    const handleLogout = async () => {
      try {
        await signOut(auth);
        localStorage.removeItem('user-info');
        userInfo.value = null;
        isLoggedIn.value = false;
        router.push('/HomePage');
      } catch (error) {
        console.error('Logout error:', error);
      }
    };

    const goToProfile = () => {
      router.push('/profile-dashboard');
    };

    const goToAdmin = () => {
      router.push('/admin-dashboard');
    };

    // Mobile menu toggle
    const toggleMobileMenu = () => {
      mobileMenuOpen.value = !mobileMenuOpen.value;
    };

    // Close mobile menu when clicking outside or on nav links
    const closeMobileMenu = () => {
      mobileMenuOpen.value = false;
    };

    // Handle keyboard navigation
    const handleKeydown = (event) => {
      if (event.key === 'Escape' && mobileMenuOpen.value) {
        closeMobileMenu();
      }
    };

    // Add global event listeners
    onMounted(() => {
      document.addEventListener('keydown', handleKeydown);
      document.addEventListener('click', (event) => {
        // Close mobile menu when clicking outside
        if (mobileMenuOpen.value && !event.target.closest('.navbar')) {
          closeMobileMenu();
        }
      });

      unsubscribe = onAuthStateChanged(auth, async (user) => {
        if (user) {
          try {
            const userData = await fetchUserData(user.uid);
            userInfo.value = {
              uid: user.uid,
              role: userData.role,
              adminRole: userData.adminRole,
              verified: userData.verified,
              firstName: userData.firstName,
              lastName: userData.lastName,
              email: userData.email,
            };
            localStorage.setItem('user-info', JSON.stringify(userInfo.value));
            isLoggedIn.value = true;
          } catch (error) {
            userInfo.value = {
              uid: user.uid,
              role: 'artist',
              verified: true,
              firstName: user.displayName?.split(' ')[0] || '',
              lastName: user.displayName?.split(' ')[1] || '',
              email: user.email,
            };
            localStorage.setItem('user-info', JSON.stringify(userInfo.value));
            isLoggedIn.value = true;
          }
        } else {
          userInfo.value = null;
          isLoggedIn.value = false;
          localStorage.removeItem('user-info');
        }
      });
    });

    onUnmounted(() => {
      if (unsubscribe) unsubscribe();
      document.removeEventListener('keydown', handleKeydown);
    });

    return {
      userInfo,
      isLoggedIn,
      isAdmin,
      isVerified,
      handleLogout,
      goToProfile,
      goToAdmin,
      openLoginModal,
      openSignUpModal,
      mobileMenuOpen,
      skipLinkFocused,
      toggleMobileMenu,
      closeMobileMenu,
    };
  },
};
</script>

<style scoped>
/* Skip Navigation Link */
.skip-link {
  position: absolute;
  top: -100px;
  left: 6px;
  background: var(--sciColor);
  color: white;
  padding: 8px;
  text-decoration: none;
  border-radius: 4px;
  z-index: 10000;
  font-weight: var(--font-weight-bold);
  transition: top 0.3s;
}

.skip-link:focus {
  top: 6px;
}

/* Mobile Menu Toggle */
.mobile-menu-toggle {
  display: none;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 50px;
  height: 44px;
  background: var(--artColor);
  border: none;
  border-radius: 10px;
  cursor: pointer;
  padding: 10px;
  position: relative;
  z-index: 1001;
  transition: all var(--stdTransition);
  gap: 0;
}

.mobile-menu-toggle::before {
  display: none;
}

.hamburger-line {
  display: block;
  position: relative;
  width: 24px;
  height: 3px;
  background: white;
  border-radius: 10px;
  opacity: 1;
  left: 0;
  margin: 3px 0;
  transition: all 0.3s ease-in-out;
  transform-origin: left center;
}

.mobile-menu-toggle.active .hamburger-line {
  left: 3px;
}

.mobile-menu-toggle.active .hamburger-line:nth-child(1) {
  transform: rotate(45deg);
}

.mobile-menu-toggle.active .hamburger-line:nth-child(2) {
  opacity: 0;
}

.mobile-menu-toggle.active .hamburger-line:nth-child(3) {
  transform: rotate(-45deg);
}

.mobile-menu-toggle:hover {
  transform: var(--hoverLift);
  box-shadow: var(--stdShadow);
}

.navbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: var(--navbarHeight);
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 1000;
  background: white;
  box-shadow: var(--stdShadow);
  padding: 0 var(--paddingTight);
}

.navbar::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0px;
  background: linear-gradient(90deg, var(--artOutlineColor) 0%, var(--sciOutlineColor) 100%);
  z-index: 1;
}

.navbar::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 2px;
  background: rgba(255, 255, 255, 0.2);
  z-index: 1;
}

.pallas-watermark {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%) translateY(-2px);
  width: 420px;
  height: var(--navbarHeight);
  z-index: 1;
  pointer-events: none;

  background: transparent;
  border: none;
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: visible;
  margin-top: -6px;
}


.pallas-watermark-text {
  width: 70%;
  height: 60%;
  background: linear-gradient(90deg, #C6A3B6 0%, #AE96BB 100%);
  /* Fallback for browsers without mask support */
  color: transparent;
  /* Modern mask properties with fallbacks */
  -webkit-mask: url('data:image/svg+xml,%3Csvg viewBox="0 0 157.18 19.62" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="black"%3E%3Cpath d="m0,19.34V1.51C0,.83.55.28,1.22.28h6.88c3.98,0,6.91,2.36,6.91,6.46s-2.93,6.46-6.91,6.46H3.07v4.92c0,.68-.55,1.22-1.22,1.22H0ZM3.07,3.04v7.39h5.4c2.5,0,3.5-.97,3.5-3.7s-1-3.7-3.5-3.7H3.07Z"%3E%3C/path%3E%3Cpath d="m27.61,19.34L35.04.28h3.23c.71,0,1.35.44,1.61,1.1l6.55,16.82c.21.55-.19,1.14-.78,1.14h-2.11l-.76-1.94c-.19-.48-.54-.88-1-1.12l-6.12-3.26c-1.04-.55-2.32-.08-2.75,1.01l-1.64,4.17c-.27.69-.95,1.15-1.69,1.15h-1.96Zm13.52-6.19l-2.6-6.55-1.22-3.53h-.23l-1.25,3.53-.79,2c-.15.38,0,.8.34,1.01l5.75,3.54Z"%3E%3C/path%3E%3Cpath d="m129.68,18.2l-6.55-16.82c-.26-.66-.9-1.1-1.61-1.10h-3.23l-7.42,19.05h2.09c.75,0,1.42-.46,1.69-1.15l.31-.79c.19-.48.54-.88,1-1.12l6.12-3.26c1.04-.55,2.32-.08,2.75,1.01l2.1,5.32h1.98c.59,0,.99-.6.78-1.14Zm-13.07-5.05l2.6-6.55,1.22-3.53h.23l1.25,3.53.79,2c.15.37,0,.8-.34,1.01l-5.75,3.54Z"%3E%3C/path%3E%3Cpath d="m59.23,18.11V.28h1.85c.68,0,1.22.55,1.22,1.22v15.07h8.93c.68,0,1.22.55,1.22,1.22v1.53h-12c-.68,0-1.22-.55-1.22-1.22Z"%3E%3C/path%3E%3Cpath d="m85.05,18.11V.28h1.85c.68,0,1.22.55,1.22,1.22v15.07h8.93c.68,0,1.22.55,1.22,1.22v1.53h-12c-.68,0-1.22-.55-1.22-1.22Z"%3E%3C/path%3E%3Cpath d="m142.34,14.25c-.05-.71.51-1.31,1.22-1.31h1.73s.02.87.03.99c.03.33.1.63.2.91.48,1.31,1.78,1.97,4.75,1.97s3.84-.77,3.84-2.53-.57-2.36-3.5-2.96l-3.24-.66c-3.27-.62-5.03-2.22-5.03-5.23,0-2.82,2.19-5.43,7.11-5.43,4.39,0,6.58,2.06,7.19,4.71.04.17.07.35.1.53.05.36.08,1.45.08,1.45h-2.36c-.37,0-.67-.29-.69-.65-.01-.28-.04-.58-.06-.72-.02-.13-.06-.25-.09-.37-.42-1.39-1.63-2.12-4.25-2.12-2.82,0-3.95.8-3.95,2.48s.77,2.22,3.38,2.79l3.24.68c3.58.74,5.15,2.53,5.15,5.4s-2.16,5.46-7.02,5.46-7.25-1.95-7.77-4.92c-.02-.09-.03-.25-.04-.45Z"%3E%3C/path%3E%3C/g%3E%3C/svg%3E') center/contain no-repeat;
  mask: url('data:image/svg+xml,%3Csvg viewBox="0 0 157.18 19.62" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="black"%3E%3Cpath d="m0,19.34V1.51C0,.83.55.28,1.22.28h6.88c3.98,0,6.91,2.36,6.91,6.46s-2.93,6.46-6.91,6.46H3.07v4.92c0,.68-.55,1.22-1.22,1.22H0ZM3.07,3.04v7.39h5.4c2.5,0,3.5-.97,3.5-3.7s-1-3.7-3.5-3.7H3.07Z"%3E%3C/path%3E%3Cpath d="m27.61,19.34L35.04.28h3.23c.71,0,1.35.44,1.61,1.1l6.55,16.82c.21.55-.19,1.14-.78,1.14h-2.11l-.76-1.94c-.19-.48-.54-.88-1-1.12l-6.12-3.26c-1.04-.55-2.32-.08-2.75,1.01l-1.64,4.17c-.27.69-.95,1.15-1.69,1.15h-1.96Zm13.52-6.19l-2.6-6.55-1.22-3.53h-.23l-1.25,3.53-.79,2c-.15.38,0,.8.34,1.01l5.75,3.54Z"%3E%3C/path%3E%3Cpath d="m129.68,18.2l-6.55-16.82c-.26-.66-.9-1.1-1.61-1.10h-3.23l-7.42,19.05h2.09c.75,0,1.42-.46,1.69-1.15l.31-.79c.19-.48.54-.88,1-1.12l6.12-3.26c1.04-.55,2.32-.08,2.75,1.01l2.1,5.32h1.98c.59,0,.99-.6.78-1.14Zm-13.07-5.05l2.6-6.55,1.22-3.53h.23l1.25,3.53.79,2c.15.37,0,.8-.34,1.01l-5.75,3.54Z"%3E%3C/path%3E%3Cpath d="m59.23,18.11V.28h1.85c.68,0,1.22.55,1.22,1.22v15.07h8.93c.68,0,1.22.55,1.22,1.22v1.53h-12c-.68,0-1.22-.55-1.22-1.22Z"%3E%3C/path%3E%3Cpath d="m85.05,18.11V.28h1.85c.68,0,1.22.55,1.22,1.22v15.07h8.93c.68,0,1.22.55,1.22,1.22v1.53h-12c-.68,0-1.22-.55-1.22-1.22Z"%3E%3C/path%3E%3Cpath d="m142.34,14.25c-.05-.71.51-1.31,1.22-1.31h1.73s.02.87.03.99c.03.33.1.63.2.91.48,1.31,1.78,1.97,4.75,1.97s3.84-.77,3.84-2.53-.57-2.36-3.5-2.96l-3.24-.66c-3.27-.62-5.03-2.22-5.03-5.23,0-2.82,2.19-5.43,7.11-5.43,4.39,0,6.58,2.06,7.19,4.71.04.17.07.35.1.53.05.36.08,1.45.08,1.45h-2.36c-.37,0-.67-.29-.69-.65-.01-.28-.04-.58-.06-.72-.02-.13-.06-.25-.09-.37-.42-1.39-1.63-2.12-4.25-2.12-2.82,0-3.95.8-3.95,2.48s.77,2.22,3.38,2.79l3.24.68c3.58.74,5.15,2.53,5.15,5.4s-2.16,5.46-7.02,5.46-7.25-1.95-7.77-4.92c-.02-.09-.03-.25-.04-.45Z"%3E%3C/path%3E%3C/g%3E%3C/svg%3E') center/contain no-repeat;
  filter: drop-shadow(2px 0 0 #C6A3B6) drop-shadow(-2px 0 0 #C6A3B6) drop-shadow(0 2px 0 #AE96BB) drop-shadow(0 -2px 0 #AE96BB);
  margin-bottom: -5px;
}

/* Browser compatibility support check */
@supports not (mask: url()) {
  .pallas-watermark-text {
    /* Fallback: display PALLAS text for browsers without mask support */
    font-family: var(--font-primary);
    font-weight: var(--font-weight-bold);
    font-size: clamp(18px, 3vw, 24px);
    display: flex;
    align-items: center;
    justify-content: center;
    color: #C6A3B6;
    text-shadow: 1px 1px 0 #AE96BB;
  }

  .pallas-watermark-text::after {
    content: 'PALLAS';
  }
}

.navbar-left {
  margin-bottom: 16px;
  margin-right: 2px;
  display: flex;
  align-items: center;
  gap: var(--paddingSpaceous);
  z-index: 10;
  position: relative;
}

.logo-link {
  display: flex;
  align-items: center;
  text-decoration: none;
  border-radius: var(--stdRadius);
}

.logo-container {
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(90deg, #FDE8E2 0%, #FAE7E3 100%) !important;
  border: none !important;
  border-radius: 14px;
  position: relative;
  height: var(--navbarHeight);
  z-index: 10;
  transform: translateY(0px);
}

.logo-link:hover {
  transform: var(--hoverLift);
  box-shadow: var(--stdShadow);
}

.logo-img {
  width: 50px;
  margin-top: 2.5px;
  height: 28px;
  object-fit: contain;
  opacity: 0;
}


.logo-container::before {
  content: '';
  position: absolute;
  inset: -4px;
  background: linear-gradient(90deg, #F9C5B4 0%, #F7C3B6 100%);
  border-radius: 14px;
  z-index: -2;
}

.logo-container::after {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(90deg, #FDE8E2 0%, #FAE7E3 100%);
  border: 4px solid transparent;
  border-radius: 14px;
  z-index: -1;
}

.logo-container .logo-gradient {
  position: absolute;
  width: 50px;
  height: 28px;
  background: linear-gradient(90deg, #F8BCAA 0%, #F5BAA9 100%);
  /* Fallback for browsers without mask support */
  background-image: url('@/assets/logomark_white.svg');
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
  /* Modern mask properties */
  -webkit-mask: url('@/assets/logomark_white.svg') center/contain no-repeat;
  mask: url('@/assets/logomark_white.svg') center/contain no-repeat;
  pointer-events: none;
  z-index: 15;
}

.nav-section {
  background: white !important;
  background-color: white !important;
  height: var(--navbarHeight);
  padding: var(--paddingStandard) var(--paddingSpaceous);
  border-radius: 14px;
  border: none !important;
  position: relative;
  z-index: 10;
  transform: translateY(-2px);
}

.nav-section::before {
  content: '';
  position: absolute;
  inset: -4px;
  background: linear-gradient(90deg, #F7C3B6 0%, #E0B4BA 100%);
  border-radius: 14px;
  z-index: -2;
}

.nav-section::after {
  content: '';
  position: absolute;
  inset: 0;
  background: white;
  border: 4px solid transparent;
  border-radius: 14px;
  z-index: -1;
}

.nav-links {
  list-style: none;
  display: flex;
  gap: var(--paddingSpaceous);
  margin: 0;
  padding: 0;
  align-items: center;
}

.nav-links li {
  position: relative;
}

.nav-link {
  font-size: var(--buttonText);
  font-weight: var(--font-weight-bold);
  color: var(--textColor);
  text-decoration: none;
  position: relative;
  transition: all var(--stdTransition);
  padding: var(--paddingTight) var(--paddingStandard);
  border-radius: calc(var(--stdRadius) / 2);
  background: none;
  transform: translateY(4px) !important;
  display: inline-block;
}

.nav-link[href="/AboutUs"] {
  background: linear-gradient(90deg, #F2B9AB 0%, #EFB8AC 100%);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}

.nav-link[href="/SearchArtists"] {
  background: linear-gradient(90deg, #ECB6AD 0%, #E8B4AD 100%);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}

.nav-link[href="/BlogPage"] {
  background: linear-gradient(90deg, #E5B2AE 0%, #E8C0BE 100%);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}

.nav-link[href="/BestPractices"] {
  background: linear-gradient(90deg, #E1B0AF 0%, #E4BEBE 100%);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}

.nav-link::after {
  content: '';
  position: absolute;
  bottom: -2px;
  left: 50%;
  width: 0;
  height: 2px;
  background: var(--art-sci-gradient);
  transition: all var(--stdTransition);
  transform: translateX(-50%);
  border-radius: 1px;
}

.nav-link.router-link-exact-active::after {
  width: 100%;
}

.nav-link[href="/AboutUs"]:hover {
  background: linear-gradient(90deg, color-mix(in srgb, #F2B9AB 50%, transparent) 0%, color-mix(in srgb, #EFB8AC 50%, transparent) 100%);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}

.nav-link[href="/SearchArtists"]:hover {
  background: linear-gradient(90deg, color-mix(in srgb, #ECB6AD 50%, transparent) 0%, color-mix(in srgb, #E8B4AD 50%, transparent) 100%);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}

.nav-link[href="/BlogPage"]:hover {
  background: linear-gradient(90deg, color-mix(in srgb, #E5B2AE 50%, transparent) 0%, color-mix(in srgb, #E8C0BE 50%, transparent) 100%);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}

.nav-link[href="/BestPractices"]:hover {
  background: linear-gradient(90deg, color-mix(in srgb, #E1B0AF 50%, transparent) 0%, color-mix(in srgb, #E4BEBE 50%, transparent) 100%);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}

.nav-link:hover::after,
.nav-link:focus::after {
  width: 100%;
}

/* Enhanced focus styles for accessibility */
.nav-link:focus {
  outline: none;
}

.auth-btn:focus {
  outline: none;
}

.logo-link:focus {
  outline: 2px solid var(--sciColor);
  outline-offset: 2px;
}

.nav-link.router-link-exact-active {
  color: transparent;
  background-image: var(--art-sci-gradient);
  -webkit-background-clip: text;
  background-clip: text;
}

.navbar-right {
  margin-top: -20px;
  display: flex;
  align-items: center;
  z-index: 10;
  position: relative;
}

.auth-section {
  display: flex;
  gap: var(--paddingStandard);
  align-items: center;
  background: transparent;
  padding: var(--paddingStandard);
  border-radius: var(--stdRadius);
  border: 4px solid transparent;
  position: relative;
}

.auth-btn {
  font-family: var(--font-primary);
  font-weight: var(--font-weight-bold);
  font-size: var(--buttonText);
  height: var(--navbarHeight);
  padding: var(--paddingStandard) var(--paddingSpaceous);
  border-radius: 10px;
  cursor: pointer;
  position: relative;
  display: inline-block;
  transition: all var(--stdTransition);
  white-space: nowrap;
  border: none;
}

.btn-text {
  margin: -6px;
  position: relative;
  z-index: 15;
  transform: translateY(12px) !important;
  display: inline-block;
}

.login-btn {
  background: white !important;
  background-color: white !important;
  border: none !important;
  position: relative;
  border-radius: 14px;
  z-index: 10;
}

.login-btn::before {
  content: '';
  position: absolute;
  inset: -4px;
  background: linear-gradient(90deg, #928DCA 0%, #8B88C9 100%);
  border-radius: 14px;
  z-index: -2;
}

.login-btn::after {
  content: '';
  position: absolute;
  inset: 0;
  background: white;
  border: 4px solid transparent;
  border-radius: 14px;
  z-index: -1;
}

.login-btn .btn-text {
  background: linear-gradient(90deg, #A09CCE 0%, #8582C4 100%);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}

.signup-btn {
  background: white !important;
  background-color: white !important;
  border: none !important;
  position: relative;
  border-radius: 14px;
  z-index: 10;
}

.signup-btn::before {
  content: '';
  position: absolute;
  inset: -4px;
  background: linear-gradient(90deg, #8B89CA 0%, #8384CB 100%);
  border-radius: 14px;
  z-index: -3;
}

.signup-btn::after {
  content: '';
  position: absolute;
  inset: 0;
  background: white;
  border: 4px solid transparent;
  border-radius: 14px;
  z-index: -2;
}

.signup-btn .btn-text {
  background: linear-gradient(90deg, #9B9AD1 0%, #7E7FC7 100%);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}

.login-btn:hover {
  transform: var(--hoverLift);
  box-shadow: var(--stdShadow);
}

.signup-btn:hover {
  transform: var(--hoverLift);
  box-shadow: var(--stdShadow);
}

.auth-btn:active {
  transform: scale(0.98);
}

.profile-btn {
  background: white !important;
  background-color: white !important;
  border: none !important;
  position: relative;
  border-radius: 14px;
  z-index: 10;
}

.profile-btn::before {
  content: '';
  position: absolute;
  inset: -4px;
  border-radius: 14px;
  z-index: -2;
}

.profile-btn::after {
  content: '';
  position: absolute;
  inset: 0;
  background: white;
  border: 4px solid transparent;
  border-radius: 14px;
  z-index: -1;
}

.profile-btn:hover {
  transform: var(--hoverLift);
  box-shadow: var(--stdShadow);
}

.profile-btn.scientist-profile::before {
  background: var(--sciColor);
}

.profile-btn.scientist-profile .btn-text {
  color: var(--sciColor);
}

.profile-btn.artist-profile::before {
  background: var(--artColor);
}

.profile-btn.artist-profile .btn-text {
  color: var(--artColor);
}

.profile-btn.both-profile::before {
  background: linear-gradient(90deg, var(--artColor) 0%, var(--sciColor) 100%);
}

.profile-btn.both-profile .btn-text {
  background: linear-gradient(90deg, var(--artColor) 0%, var(--sciColor) 100%);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}

.admin-btn {
  background: white !important;
  background-color: white !important;
  border: none !important;
  position: relative;
  border-radius: 14px;
  z-index: 10;
}

.admin-btn::before {
  content: '';
  position: absolute;
  inset: -4px;
  background: linear-gradient(90deg, var(--sciColor) 0%, var(--artColor) 100%);
  border-radius: 14px;
  z-index: -2;
}

.admin-btn::after {
  content: '';
  position: absolute;
  inset: 0;
  background: white;
  border: 4px solid transparent;
  border-radius: 14px;
  z-index: -1;
}

.admin-btn .btn-text {
  background: linear-gradient(90deg, var(--sciColor) 0%, var(--artColor) 100%);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}

.admin-btn:hover {
  transform: var(--hoverLift);
  box-shadow: var(--stdShadow);
}

.logout-btn {
  background: linear-gradient(90deg, #D3D3EB 0%, #CFCFE9 100%) !important;
  border: 4px solid white !important;
  position: relative;
  border-radius: 14px;
  z-index: 10;
}

.logout-btn::before {
  display: none !important;
}

.logout-btn::after {
  display: none !important;
}

.logout-btn .btn-text {
  color: white !important;
  background: none !important;
  -webkit-background-clip: initial !important;
  background-clip: initial !important;
}

.logout-btn:hover {
  transform: var(--hoverLift);
  box-shadow: var(--stdShadow);
}

.profile-section {
  display: flex;
  gap: var(--paddingStandard);
  align-items: center;
  padding: var(--paddingStandard);
}

@media (max-width: var(--breakpointTablet)) {
  .navbar {
    padding: 0 var(--paddingStandard);
  }
  
  .pallas-watermark {
    width: 280px;
    height: 22px;
  }
  
  .navbar-left {
    gap: var(--paddingStandard);
  }
  
  .nav-links {
    gap: var(--paddingStandard);
  }
  
  .nav-link {
    font-size: var(--labelText);
    padding: var(--paddingTight);
  }
  
  .auth-btn {
    font-size: var(--labelText);
    padding: var(--paddingTight) var(--paddingStandard);
  }
  
  .logo-container {
    width: 44px;
    height: 44px;
  }
  
  .logo-img {
    width: 24px;
    height: 24px;
  }
}

@media (max-width: 600px) {
  .mobile-menu-toggle {
    display: flex;
  }

  .nav-section {
    position: fixed;
    top: var(--navbarHeight);
    left: -100%;
    width: 100%;
    height: calc(100vh - var(--navbarHeight));
    background: white;
    z-index: 1000;
    transition: left 0.3s ease;
    overflow-y: auto;
    border-top: 2px solid;
    border-image: linear-gradient(90deg, var(--artColor) 0%, var(--sciColor) 100%) 1;
    border-radius: 0;
    padding: var(--paddingStandard);
  }

  .nav-section.mobile-open {
    left: 0;
  }

  .nav-section.mobile-open::before,
  .nav-section.mobile-open::after {
    display: none;
  }

  .nav-links {
    flex-direction: column;
    padding: var(--paddingSpaceous);
    gap: var(--paddingStandard);
  }

  .nav-link {
    padding: var(--paddingStandard);
    width: 100%;
    text-align: center;
    border-radius: var(--stdRadius);
    background: rgba(255, 255, 255, 0.8);
  }

  .pallas-watermark {
    width: 220px;
    height: 18px;
  }

  .auth-section {
    gap: var(--paddingTight);
  }

  .profile-section {
    gap: var(--paddingTight);
  }

  .auth-btn {
    font-size: var(--labelText);
    padding: var(--paddingTight) var(--paddingStandard);
  }
}
</style>